// hooks/useFileUpload.ts
import { useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "utils/axiosConfig";
import { languages } from "data/languages";
import { toast } from "hooks/use-toast";
import { confirm } from "providers/ConfirmationProvider";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useProjectValue } from "components/projects/ProjectDraft";

// ---------- types ----------
export interface FileCost {
  translate: string;
  voiceover: string;
  transcript: string;
}

export const FILETYPE_YOUTUBE_LINK = "youtubelink";
export const FILETYPE_YOUTUBE_FAILED = "youtubelink_failed";

export interface FileData {
  id: string;
  filename: string;
  filetype: string;
  original_language?: string | null;
  is_multiple_voices?: boolean;
  voice_tags?: string[];
  youtube_import_status?: string | null;
  youtube_import_error?: string | null;
}

function mapFileStatusPayload(data: Record<string, unknown>): FileData {
  return {
    id: String(data.id),
    filename: String(data.filename ?? ""),
    filetype: String(data.filetype ?? ""),
    original_language: (data.original_language as string) ?? null,
    is_multiple_voices: data.is_multiple_voices as boolean | undefined,
    voice_tags: data.voice_tags as string[] | undefined,
    youtube_import_status: (data.youtube_import_status as string) ?? null,
    youtube_import_error: (data.youtube_import_error as string) ?? null,
  };
}

interface Voice {
  id: string;
  name: string;
}

interface Tag {
  id: string;
  name: string;
}

export const useFileUpload = () => {
  const { fileId, projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /* ------------- state ------------- */
  const [fileData, setFileData] = useState<FileData | null>(null);
  const [fileCost, setFileCost] = useState<FileCost | null>(null);

  // Tags fetched from API
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagId, setSelectedTagId] = useProjectValue<string>("selectedTagId", "");
  const [newTagName, setNewTagName] = useProjectValue<string>("newTagName", "");

  const [availableLanguages, setAvailableLanguages] = useState(languages);
  const [selectedLanguage, setSelectedLanguage] = useProjectValue("selectedLanguage", languages[0]?.code || "");
  const [selectedVoice, setSelectedVoice] = useProjectValue("selectedVoice", "");
  const [voices, setVoices] = useState<Voice[]>([]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [label, setLabel] = useProjectValue("label", "");

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [audioType, setAudioType] = useState<boolean>(false);
  const [stepByStep, setStepByStep] = useProjectValue("stepByStep", false);

  const [multipleVoicesMode, setMultipleVoicesMode] = useProjectValue("multipleVoicesMode", false);
  const [voiceMapping, setVoiceMapping] = useProjectValue<Record<string, string>>("voiceMapping", {});
  const [voiceMappingDefault, setVoiceMappingDefault] = useProjectValue<string>("voiceMappingDefault", "");

  /* ------------- cost/status helpers ------------- */
  const fetchCostWithRetries = useCallback(async (fileId: string, delay = 3) => {
    // Define sleep inside the callback to avoid dependency issues
    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

    let wait = delay;
    for (let attempt = 0; attempt < 5; attempt++) {
      setLoadingMessage(`Calculating cost… retry in ${wait}s`);
      try {
        const { data } = await axios.get<{
          translation_cost: { raw: string; value: number };
          voiceover_cost: { raw: string; value: number };
          transcription_cost: { raw: string; value: number };
          voiceover_quota?: { ok: boolean; message?: string | null };
        }>(`/file/${fileId}/cost`, {
          params: selectedVoice ? { voiceId: selectedVoice } : undefined,
        });
        if (data.translation_cost.value > 0) {
          setFileCost({
            translate: data.translation_cost.raw,
            voiceover: data.voiceover_cost.raw,
            transcript: data.transcription_cost.raw,
          });
          // Store quota warning if present
          if (data.voiceover_quota && !data.voiceover_quota.ok) {
            setVoiceoverQuotaWarning(data.voiceover_quota.message || "ElevenLabs quota exceeded.");
          } else {
            setVoiceoverQuotaWarning("");
          }
          setLoadingMessage("");
          return;
        }
      } catch (err) {
        console.error("Cost error", err);
      }
      await sleep(wait * 1000);
      wait *= 2;
    }
    setFileCost(null);
    setLoadingMessage("");
    toast({
      title: "Warning",
      description: "Unable to determine cost.",
      variant: "destructive",
    });
  }, []); // Remove sleep from dependencies

  const fetchFileStatusWithRetries = useCallback(
    async (fileId: string, delay = 3, max = 5) => {
      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

      let wait = delay * 1000;
      for (let attempt = 0; attempt < max; attempt++) {
        setLoadingMessage(
          `Waiting for original language… (${attempt + 1}/${max})`
        );
        try {
          const { data } = await axios.get<FileData>(
            `/v2/file/${fileId}/status`
          );
          if (data.original_language) {
            setFileData(data);
            setLoadingMessage("");
            return;
          }
        } catch (err) {
          console.error("Status error", err);
        }
        await sleep(wait);
        wait *= 2;
      }
      setLoadingMessage("");
      toast({
        title: "Warning",
        description: "Original language not detected.",
        variant: "destructive",
      });
    },
    []
  );

  const pollYoutubeImportUntilSettled = useCallback(async (fileId: string) => {
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    const deadline = Date.now() + 16 * 60 * 1000;
    while (Date.now() < deadline) {
      try {
        const { data } = await axios.get<Record<string, unknown>>(
          `/v2/file/${fileId}/status`
        );
        const ft = String(data.filetype ?? "");
        if (ft === FILETYPE_YOUTUBE_LINK) {
          await sleep(2500);
          continue;
        }
        if (ft === FILETYPE_YOUTUBE_FAILED) {
          return { kind: "failed" as const, data: mapFileStatusPayload(data) };
        }
        return { kind: "ready" as const, data: mapFileStatusPayload(data) };
      } catch (err: unknown) {
        const status =
          err &&
          typeof err === "object" &&
          "response" in err &&
          (err as { response?: { status?: number } }).response?.status;
        if (status === 404) {
          return { kind: "gone" as const };
        }
        await sleep(2500);
      }
    }
    return { kind: "gone" as const };
  }, []);

  const [voiceoverQuotaWarning, setVoiceoverQuotaWarning] = useState<string>("");

  /* ------------- drop-zone ------------- */
  const onDrop = useCallback((accepted: File[]) => {
    if (!accepted.length) return;
    setSelectedFile(accepted[0]);
    setYoutubeUrl("");
  }, []);

  const blockNewUpload =
    fileData?.filetype === FILETYPE_YOUTUBE_LINK ||
    fileData?.filetype === FILETYPE_YOUTUBE_FAILED;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    disabled: blockNewUpload,
    maxFiles: 1,
    accept: {
      "text/plain": [".txt"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
      "application/vnd.oasis.opendocument.text": [".odt"],
      "audio/mpeg": [".mp3"],
      "audio/wav": [".wav"],
    },
  });

  /* ------------- fetch tags ------------- */
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const { data } = await axios.get<Tag[]>("/v2/user/tags");
        setTags(data);
        // default to first or empty
        setSelectedTagId(previous => previous || (data.length > 0 ? String(data[0].id) : ""));
      } catch (err) {
      }
    };
    fetchTags();
  }, []);

  /* ------------- other side-effects ------------- */
  useEffect(() => {
    const fetchPending = async () => {
      try {
        setLoading(true);
        if (!fileId && searchParams.get("new") === "1") return;
        setLoadingMessage("Opening your source…");
        const { data } = await axios.get<Record<string, unknown> | null>(
          fileId ? `/v2/file/${fileId}/status` : "/v2/files/pending"
        );
        if (!data) return;

        const mapped = mapFileStatusPayload(data);
        setFileData(mapped);
        if (!projectId) setLabel(mapped.filename);
        setUploadError("");

        if (
          mapped.filetype.includes("audio") ||
          mapped.filetype === FILETYPE_YOUTUBE_LINK
        ) {
          setAudioType(true);
        } else {
          setAudioType(false);
        }

        if (mapped.is_multiple_voices) {
          setMultipleVoicesMode(true);
        }

        if (mapped.filetype === FILETYPE_YOUTUBE_LINK) {
          setLoading(false);
          setLoadingMessage("");
          const outcome = await pollYoutubeImportUntilSettled(mapped.id);
          if (outcome.kind === "gone") {
            setFileData(null);
            setLabel("");
            setUploadError(
              "YouTube import timed out or was cancelled. You can start a new upload."
            );
            return;
          }
          if (outcome.kind === "failed") {
            setFileData(outcome.data);
            setLabel(outcome.data.filename);
            setUploadError(
              outcome.data.youtube_import_error ??
                "YouTube import failed. Remove the file to try again."
            );
            return;
          }
          setFileData(outcome.data);
          setLabel(outcome.data.filename);
          if (outcome.data.filetype.includes("audio")) {
            setAudioType(true);
          }
          await fetchCostWithRetries(outcome.data.id, 3);
          if (!outcome.data.original_language) {
            await fetchFileStatusWithRetries(outcome.data.id);
          }
          return;
        }

        if (mapped.filetype === FILETYPE_YOUTUBE_FAILED) {
          setUploadError(
            mapped.youtube_import_error ??
              "YouTube import failed. Remove the file to try again."
          );
          return;
        }

        await fetchCostWithRetries(mapped.id, 3);
        if (!mapped.original_language) {
          await fetchFileStatusWithRetries(mapped.id);
        }
      } catch (err) {
        console.error("Pending file error", err);
      } finally {
        setLoading(false);
        setLoadingMessage("");
      }
    };
    fetchPending();
  }, [
    fetchCostWithRetries,
    fetchFileStatusWithRetries,
    pollYoutubeImportUntilSettled,
    fileId, projectId, searchParams,
  ]);

  useEffect(() => {
    if (fileData?.original_language) {
      // filter out the original language from the list
      const filtered = languages.filter(
        (l) => l.iso !== fileData.original_language
      );
      setAvailableLanguages(filtered);

      if (selectedLanguage ===
        languages.find((l) => l.iso === fileData.original_language)?.code
      ) {
        setSelectedLanguage(filtered[0]?.code || "");
      }
    } else {
      // if no original language, set all languages
      setAvailableLanguages(languages);
    }
  }, [fileData?.original_language, selectedLanguage]);

  useEffect(() => {
    const loadVoices = async () => {
      try {
        const { data } = await axios.get<Voice[]>("/v2/user/languages");
        setVoices(data);
        if (data.length) setSelectedVoice(previous => previous || data[0].id);
      } catch (err) {
        console.error("Voices error", err);
        toast({
          title: "Error",
          description: "Failed to load voices.",
          variant: "destructive",
        });
      }
    };
    loadVoices();
  }, []);

  /* ------------- actions ------------- */
  const handleUpload = async () => {
    if (!selectedFile && !youtubeUrl) return;
    setUploadError("");
    setLoading(true);
    setLoadingMessage("Uploading…");

    try {
      const formData = new FormData();
      if (selectedFile) formData.append("file", selectedFile);
      if (youtubeUrl) formData.append("youtube_url", youtubeUrl);

      const { data } = await axios.post<Record<string, unknown>>(
        "/v2/file/upload",
        formData
      );

      if (data.duplicate) {
        toast({ title: "Upload successful", description: "This file was already uploaded, so we reused the existing file." });
      }

      if (data.project_id) {
        navigate(`/projects/${data.project_id}/source/${data.file_id}`);
        return;
      }
      const uploaded = mapFileStatusPayload({
        id: data.file_id,
        filename: data.filename,
        filetype: data.filetype,
        original_language: data.original_language,
        is_multiple_voices: data.is_multiple_voices,
        voice_tags: data.voice_tags,
        youtube_import_status: data.youtube_import_status,
        youtube_import_error: data.youtube_import_error,
      });
      setFileData(uploaded);
      setLabel(uploaded.filename);

      if (uploaded.is_multiple_voices) setMultipleVoicesMode(true);

      if (
        uploaded.filetype.includes("audio") ||
        uploaded.filetype === FILETYPE_YOUTUBE_LINK
      ) {
        setAudioType(true);
      } else {
        setAudioType(false);
      }

      setSelectedFile(null);
      setYoutubeUrl("");

      if (uploaded.filetype === FILETYPE_YOUTUBE_LINK) {
        setLoading(false);
        setLoadingMessage("");
        const outcome = await pollYoutubeImportUntilSettled(uploaded.id);
        if (outcome.kind === "gone") {
          setFileData(null);
          setLabel("");
          setUploadError(
            "YouTube import timed out or was cancelled. You can start a new upload."
          );
          return;
        }
        if (outcome.kind === "failed") {
          setFileData(outcome.data);
          setLabel(outcome.data.filename);
          setUploadError(
            outcome.data.youtube_import_error ??
              "YouTube import failed. Remove the file to try again."
          );
          return;
        }
        setFileData(outcome.data);
        setLabel(outcome.data.filename);
        await fetchCostWithRetries(outcome.data.id, 3);
        if (!outcome.data.original_language) {
          await fetchFileStatusWithRetries(outcome.data.id);
        }
        return;
      }

      await fetchCostWithRetries(uploaded.id, 3);
      if (!uploaded.original_language) {
        await fetchFileStatusWithRetries(uploaded.id);
      }
    } catch (err: any) {
      console.error("Upload error", err);
      const msg = err?.response?.data?.detail ?? "Upload failed. Please try again.";
      setUploadError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const handleAction = async (
    action: "translation" | "voiceover" | "transcript"
  ) => {
    if (!fileData || actionLoading) return;
    if (
      fileData.filetype === FILETYPE_YOUTUBE_LINK ||
      fileData.filetype === FILETYPE_YOUTUBE_FAILED
    ) {
      return;
    }

    // build request body including tag
    const tagValue = newTagName.trim() !== "" ? newTagName.trim() : selectedTagId;
    const body: Record<string, any> = { action, label, tag: tagValue };
    if (action !== "transcript") body.language = selectedLanguage;
    if (action === "voiceover") {
      body.voice_id = selectedVoice;
      body.step_by_step = stepByStep;
      body.is_multiple_voices = multipleVoicesMode;
      if (multipleVoicesMode) {
        body.voice_mapping = {
          ...voiceMapping,
          default: voiceMappingDefault || selectedVoice,
        };
      }
    }

    setActionLoading(true);
    try {
      const { data } = await axios.post<{ url: string }>(
        `/v2/file/${fileData.id}/action`,
        body
      );
      toast({ title: "Success", description: "Task created." });
      if (data.url) {
        if (data.url.startsWith("/projects/")) navigate(data.url);
        else if (projectId && data.url === "/") navigate(`/projects/${projectId}`);
        else if (projectId && data.url.startsWith("/voiceover/step-by-step/")) navigate(`/projects/${projectId}/voice/${data.url.split("/").pop()}`);
        else window.location.href = data.url;
      }
    } catch (err) {
      console.error("Action error", err);
      toast({
        title: "Error",
        description: "Action failed.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!fileData) return;
    confirm("Delete this file?", async () => {
      setActionLoading(true);
      try {
        await axios.delete(`/v2/file/${fileData.id}`);
        setFileData(null);
        setFileCost(null);
        setLabel("");
        setSelectedFile(null);
        setYoutubeUrl("");
        setUploadError("");
        toast({ title: "Deleted", description: "File removed." });
      } catch (err) {
        console.error("Delete error", err);
      } finally {
        setActionLoading(false);
      }
    });
  };

  /* ------------- return ------------- */
  const isYoutubeImportWaiting = fileData?.filetype === FILETYPE_YOUTUBE_LINK;
  const isYoutubeImportFailed = fileData?.filetype === FILETYPE_YOUTUBE_FAILED;

  return {
    fileData,
    fileCost,
    loading,
    loadingMessage,
    uploadError,
    isYoutubeImportWaiting,
    isYoutubeImportFailed,
    blockNewUpload,
    selectedFile,
    setSelectedFile,
    youtubeUrl,
    label,
    selectedLanguage,
    availableLanguages,
    selectedVoice,
    voices,
    tags,
    selectedTagId,
    setSelectedTagId,
    newTagName,
    audioType,
    setNewTagName,
    actionLoading,
    setYoutubeUrl,
    setLabel,
    setSelectedLanguage,
    setSelectedVoice,
    getRootProps,
    getInputProps,
    handleUpload,
    handleAction,
    handleDelete,
    voiceoverQuotaWarning,
    stepByStep,
    setStepByStep,
    multipleVoicesMode,
    setMultipleVoicesMode,
    voiceMapping,
    setVoiceMapping,
    voiceMappingDefault,
    setVoiceMappingDefault,
  };
};
